import React from 'react';
import { 
  Box, styled, AppBar, Toolbar, Typography, IconButton, Avatar, Menu, MenuItem, Badge,
  List, ListItem, ListItemButton, ListItemText, ListItemIcon, Divider, Chip, Stack, useMediaQuery, useTheme, Dialog, DialogTitle, DialogContent, DialogActions, Button, Fade
} from '@mui/material';
import { useState, useEffect, useCallback } from 'react';
import Sidebar from './Sidebar';
import { useAuth } from '../../context/AuthContext';
import { useNotificationSettings } from '../../contexts/NotificationSettingsContext';
import { useNavigate } from 'react-router-dom';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SettingsIcon from '@mui/icons-material/Settings';
import PersonIcon from '@mui/icons-material/Person';
import CircleIcon from '@mui/icons-material/Circle';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import WarningIcon from '@mui/icons-material/Warning';
import { t } from '../../utils/textUtils';
import api from '../../services/api';
import { registerWebDeviceToken, subscribeForegroundNotification } from '../../services/fcmClient';
import { toast } from 'react-toastify';
import { getRoleConfig } from '../../utils/roleConfig';

const DRAWER_WIDTH = 280;
const COLLAPSED_WIDTH = 72;

const MainContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  minHeight: '100vh',
  backgroundColor: '#FFF8E1', // Warna background sesuai dengan desain dashboard-v1.png
  overflow: 'hidden', // Prevent scrollbar from appearing unnecessarily
}));

const AppBarStyled = styled(AppBar, { shouldForwardProp: (prop) => prop !== 'hideSidebar' && prop !== 'collapsed' })(({ theme, hideSidebar, collapsed }) => ({
  width: hideSidebar ? '100%' : `calc(100% - ${collapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH}px)`,
  marginLeft: hideSidebar ? 0 : (collapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH),
  backgroundColor: 'rgba(255,255,255,0.95)',
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
  boxShadow: '0 1px 12px rgba(15, 23, 42, 0.06)',
  borderBottom: '1px solid #e2e8f0',
  color: '#5D4037',
  zIndex: theme.zIndex.drawer + 1,
  transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1), margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
}));

const ContentContainer = styled(Box, { shouldForwardProp: (prop) => prop !== 'hideSidebar' && prop !== 'collapsed' })(({ theme, hideSidebar, collapsed }) => ({
  flexGrow: 1,
  padding: hideSidebar ? theme.spacing(2) : theme.spacing(2, 2, 2, collapsed ? 2 : 3),
  backgroundColor: '#f5f7fa',
  marginTop: 64,
  minHeight: 'calc(100vh - 64px)',
  overflow: 'auto',
  width: hideSidebar ? '100%' : `calc(100% - ${collapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH}px)`,
  transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1), padding 0.3s ease',
}));

const MainLayout = ({ children, hideSidebar = false }) => {
  const { user, logout } = useAuth();
  const { isEnabled, intervalMs } = useNotificationSettings();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState(null);
  const [notificationCount, setNotificationCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const audioCtxRef = React.useRef(null);
  const userGestureRef = React.useRef(false);
  const [readIds, setReadIds] = useState(() => {
    try {
      const stored = localStorage.getItem('notification_read_ids');
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch {
      return new Set();
    }
  });
  
  const handleSidebarToggle = useCallback(() => {
    setSidebarCollapsed(prev => !prev);
  }, []);
  
  const handleMobileDrawerToggle = useCallback(() => {
    setMobileDrawerOpen(prev => !prev);
  }, []);
  
  const handleMobileDrawerClose = useCallback(() => {
    setMobileDrawerOpen(false);
  }, []);
  const persistReadIds = (ids) => {
    try {
      localStorage.setItem('notification_read_ids', JSON.stringify(Array.from(ids)));
    } catch {
      // ignore
    }
  };
  
  const open = Boolean(anchorEl);
  const notificationOpen = Boolean(notificationAnchorEl);
  
  // Check user role for menu access control
  const isAdmin = user && (user.role === 'Director' || user.role === 'admin');
  const isCommercial = user && ['commercial_manager', 'commercial_staff'].includes(user.role);
  const hasSettingsAccess = isAdmin || isCommercial;
  const roleConfig = getRoleConfig(user?.role);
  
  // Debug logging for role detection in MainLayout
  // console.log('%c MainLayout Role Detection', 'background: #222; color: #ff6b6b; font-size: 16px;');
  // console.log('MainLayout - User role:', user?.role);
  // console.log('MainLayout - roleConfig:', roleConfig);
  // console.log('MainLayout - isAdmin:', isAdmin);
  
  // Fetch notification count and recent notifications
  useEffect(() => {
    if (!user) {
      setNotificationCount(0);
      setNotifications([]);
      return;
    }

    // Always fetch once on mount/open
    fetchNotificationCount();
    fetchRecentNotifications();
    
    // Set up polling for real-time updates based on settings
    let interval;
    if (isEnabled) {
      interval = setInterval(() => {
        fetchNotificationCount();
        if (notificationOpen) {
          fetchRecentNotifications();
        }
      }, intervalMs);
    }
    
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [user, notificationOpen, isEnabled, intervalMs]);

  useEffect(() => {
    let unsubscribe;
    if (user) {
      registerWebDeviceToken().catch((err) => {
        console.warn('FCM registration skipped:', err?.message || err);
      });
      unsubscribe = subscribeForegroundNotification((payload) => {
        const notification = payload?.notification || {};
        const data = payload?.data || {};
        setNotificationCount((prev) => prev + 1);
        if (notification.title || notification.body) {
          toast.info(
            `${notification.title || 'Notifikasi'}${notification.body ? `: ${notification.body}` : ''}`
          );
        }
        if (data.web_link) {
          fetchRecentNotifications();
          // Attempt to open deep link/web link when clicking on toast
          // (background clicks handled in service worker)
          const url = data.web_link;
          if (url && window && typeof window.open === 'function') {
            // No auto-open to avoid intrusive behavior; keep for future hook
          }
        }
        playNotificationSound();
      });
    }

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [user]);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const handler = (event) => {
      const data = event.data || {};
      if (data.type === 'OPEN_URL' && data.url) {
        window.focus();
        window.location.href = data.url;
      }
      if (data.type === 'PLAY_SOUND') {
        playNotificationSound();
      }
    };
    navigator.serviceWorker.addEventListener('message', handler);
    return () => navigator.serviceWorker.removeEventListener('message', handler);
  }, []);

  // Try to unlock audio context on first user gesture to avoid autoplay block
  useEffect(() => {
    const resumeAudio = () => {
      userGestureRef.current = true;
      if (audioCtxRef.current && audioCtxRef.current.state === 'suspended') {
        audioCtxRef.current.resume().catch(() => {});
      }
      window.removeEventListener('click', resumeAudio);
      window.removeEventListener('keydown', resumeAudio);
    };
    window.addEventListener('click', resumeAudio);
    window.addEventListener('keydown', resumeAudio);
    return () => {
      window.removeEventListener('click', resumeAudio);
      window.removeEventListener('keydown', resumeAudio);
    };
  }, []);

  const playNotificationSound = () => {
    try {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === 'suspended' && userGestureRef.current) {
        ctx.resume().catch(() => {});
      }
      if (ctx.state !== 'running') return; // bail if autoplay still blocked

      const createBeep = (startOffset) => {
        const oscillator = ctx.createOscillator();
        const gain = ctx.createGain();
        oscillator.type = 'triangle';
        oscillator.frequency.value = 1000; // Hz
        const start = ctx.currentTime + startOffset;
        gain.gain.setValueAtTime(0.15, start);
        gain.gain.exponentialRampToValueAtTime(0.001, start + 0.3);
        oscillator.connect(gain);
        gain.connect(ctx.destination);
        oscillator.start(start);
        oscillator.stop(start + 0.3);
      };

      // Play 3 short beeps in quick succession
      createBeep(0);
      createBeep(0.12);
      createBeep(0.24);
    } catch (e) {
      // Ignore audio errors silently
    }
  };

  const mapEventNotifications = (items, readSet = readIds) => {
    return items.map((n) => ({
      ...n,
      link: n.webLink || n.deepLink || n.link || n.payload?.web_link,
      webLink: n.webLink || n.payload?.web_link,
      isRead: n.isRead || readSet.has(n.id) || false,
      priority: n.priority || 'medium'
    }));
  };

  const fetchNotificationCount = async () => {
    try {
      // Use event log count for accuracy
      const response = await api.get('/notifications/events?limit=200');
      const items = mapEventNotifications(response.data.notifications || [], readIds);
      // We track unread locally (marking read not persisted), so derive from state if available
      const unread = items.filter((n) => !n.isRead).length || items.length;
      setNotificationCount(unread);
      // Keep dropdown list aligned with latest unread events only
      const unreadItems = items.filter((n) => !n.isRead);
      setNotifications(unreadItems.slice(0, 5));
    } catch (error) {
      console.error('Error fetching notification count:', error);
    }
  };

  const fetchRecentNotifications = async () => {
    try {
      setLoading(true);
      const response = await api.get('/notifications/events?limit=200');
      const mapped = mapEventNotifications(response.data.notifications || [], readIds);
      const unreadItems = mapped.filter((n) => !n.isRead);
      setNotifications(unreadItems.slice(0, 5));
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  
  const handleNotificationMenu = (event) => {
    setNotificationAnchorEl(event.currentTarget);
    fetchRecentNotifications();
  };

  const handleNotificationClose = () => {
    setNotificationAnchorEl(null);
  };

  const handleNotificationClick = async (notification) => {
    try {
      // Mark as read locally (event-based notifications don't persist read state)
      if (!notification.isRead) {
        setNotifications(prev =>
          prev.map(n => n.id === notification.id ? { ...n, isRead: true } : n)
        );
        setNotificationCount(prev => Math.max(0, prev - 1));
        const updated = new Set(readIds);
        updated.add(notification.id);
        setReadIds(updated);
        persistReadIds(updated);
      }
      
      // Navigate to link if available
      const targetLink = notification.link || notification.webLink || notification.deepLink || notification.data?.web_link;
      if (targetLink) {
        // If absolute URL, use window.location
        if (targetLink.startsWith('http')) {
          window.location.href = targetLink;
        } else {
          navigate(targetLink);
        }
      }
      
      handleNotificationClose();
    } catch (error) {
      console.error('Error handling notification click:', error);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return '#f44336';
      case 'medium': return '#ff9800';
      case 'low': return '#4caf50';
      default: return '#9e9e9e';
    }
  };

  const formatNotificationTime = (createdAt) => {
    const now = new Date();
    const notifTime = new Date(createdAt);
    const diffInMinutes = Math.floor((now - notifTime) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };
  


  const handleLogout = () => {
    setLogoutDialogOpen(true);
  };
  
  const handleLogoutConfirm = () => {
    setLogoutDialogOpen(false);
    handleClose();
    logout();
    navigate('/login');
    toast.success('Logged out successfully');
  };
  
  const handleLogoutCancel = () => {
    setLogoutDialogOpen(false);
  };

  return (
    <MainContainer>
      {!hideSidebar && (
        <Sidebar 
          collapsed={sidebarCollapsed} 
          onToggle={handleSidebarToggle}
          mobileOpen={mobileDrawerOpen}
          onMobileClose={handleMobileDrawerClose}
        />
      )}
      <AppBarStyled position="fixed" hideSidebar={hideSidebar} collapsed={sidebarCollapsed ? 1 : 0}>
        <Toolbar sx={{ justifyContent: 'space-between', minHeight: '64px', px: { xs: 1.5, sm: 2 } }}>
          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
            {!hideSidebar && isMobile && (
              <IconButton
                edge="start"
                color="inherit"
                aria-label="open drawer"
                onClick={handleMobileDrawerToggle}
                sx={{ mr: 1, color: '#5D4037' }}
              >
                <MenuIcon />
              </IconButton>
            )}
            <Avatar
              variant="rounded"
              sx={{
                width: 36,
                height: 36,
                bgcolor: '#0f172a',
                color: '#f8fafc',
                fontWeight: 800,
                fontSize: '0.85rem',
                borderRadius: 2,
                boxShadow: '0 4px 12px rgba(15,23,42,0.25)'
              }}
            >
              HS
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  color: '#3e2723',
                  fontWeight: 800,
                  lineHeight: 1.1,
                  letterSpacing: 0.2,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: { xs: 180, sm: 380, md: 520 }
                }}
              >
                {t('HAKIKI STEEL')}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: '#64748b',
                  fontWeight: 700,
                  letterSpacing: 0.35,
                  textTransform: 'uppercase'
                }}
              >
                {t('SISTEM MANAJEMEN PROYEK')}
              </Typography>
            </Box>
          </Stack>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {/* Notification Icon with Badge */}
            <IconButton 
              size="medium" 
              color="inherit" 
              sx={{
                mr: 0.25,
                border: '1px solid #e2e8f0',
                bgcolor: '#ffffff',
                '&:hover': { bgcolor: '#f8fafc' }
              }}
              onClick={handleNotificationMenu}
            >
              <Badge badgeContent={notificationCount} color="error">
                <NotificationsIcon fontSize="small" />
              </Badge>
            </IconButton>
            
            {/* Notification Menu */}
            <Menu
              id="notification-menu"
              anchorEl={notificationAnchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={notificationOpen}
              onClose={handleNotificationClose}
              PaperProps={{
                sx: { 
                  width: 350, 
                  maxHeight: 400,
                  mt: 1
                }
              }}
            >
              <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  {t("Notifications")}
                </Typography>
                {notificationCount > 0 && (
                  <Chip 
                    label={`${notificationCount} unread`} 
                    size="small" 
                    color="error" 
                    sx={{ mt: 0.5 }}
                  />
                )}
              </Box>
              
              {loading ? (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Loading...
                  </Typography>
                </Box>
              ) : notifications.length === 0 ? (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    No notifications
                  </Typography>
                </Box>
              ) : (
                <List sx={{ p: 0 }}>
                  {notifications.map((notification, index) => (
                    <React.Fragment key={notification.id}>
                      <ListItem disablePadding>
                        <ListItemButton
                          onClick={() => handleNotificationClick(notification)}
                          sx={{
                            backgroundColor: notification.isRead ? 'transparent' : '#f5f5f5',
                            '&:hover': { backgroundColor: '#e3f2fd' }
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <CircleIcon 
                              sx={{ 
                                fontSize: 8, 
                                color: getPriorityColor(notification.priority || 'medium'),
                                opacity: notification.isRead ? 0.3 : 1
                              }} 
                            />
                          </ListItemIcon>
                          <ListItemText
                            disableTypography
                            primary={
                              <Typography 
                                variant="body2" 
                                component="div"
                                sx={{ 
                                  fontWeight: notification.isRead ? 'normal' : 'bold',
                                  fontSize: '0.875rem'
                                }}
                              >
                                {notification.title || notification.message || 'Notifikasi'}
                              </Typography>
                            }
                            secondary={
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                                <Typography variant="caption" component="span" color="text.secondary" sx={{ maxWidth: '65%' }} noWrap>
                                  {notification.message || notification.Project?.name || 'System'}
                                </Typography>
                                <Typography variant="caption" component="span" color="text.secondary">
                                  {formatNotificationTime(notification.createdAt)}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                      {index < notifications.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              )}
              
              <Divider />
              <MenuItem onClick={() => { handleNotificationClose(); navigate('/notifications'); }}>
                <Typography variant="body2" sx={{ textAlign: 'center', width: '100%', color: 'primary.main' }}>
                  {t("View All Notifications")}
                </Typography>
              </MenuItem>
            </Menu>
            

            
            {/* User Profile */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                ml: 0.25,
                px: { xs: 0.6, sm: 1.1 },
                py: 0.5,
                borderRadius: 999,
                border: '1px solid #e2e8f0',
                bgcolor: '#ffffff',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': { 
                  bgcolor: '#f8fafc',
                  borderColor: '#cbd5e1',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 12px rgba(15, 23, 42, 0.1)'
                },
                '&:active': {
                  transform: 'translateY(0)',
                }
              }}
              onClick={handleMenu}
            >
              <Avatar 
                src={user?.profilePicture ? `${user.profilePicture}?t=${new Date().getTime()}` : undefined}
                sx={{ 
                  bgcolor: '#F0C14B',
                  width: 34,
                  height: 34,
                  mr: 1.1,
                  fontSize: '0.9rem',
                  fontWeight: 'bold',
                  border: '1px solid #e2e8f0'
                }}
              >
                {user?.username?.charAt(0).toUpperCase() || 'U'}
              </Avatar>
              <Box sx={{ display: { xs: 'none', sm: 'block' }, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#5D4037',
                    fontWeight: 700,
                    lineHeight: 1.1,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: 170
                  }}
                >
                  {user?.fullName || user?.name || 'User'}
                </Typography>
                <Chip
                  label={roleConfig.label}
                  size="small"
                  style={{
                    backgroundColor: roleConfig.bg,
                    color: roleConfig.color,
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    height: 20,
                  }}
                />
              </Box>
            </Box>
            
            {/* User Menu */}
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={open}
              onClose={handleClose}
              PaperProps={{
                sx: {
                  minWidth: 200,
                  mt: 1.5,
                  borderRadius: 2,
                  boxShadow: '0 8px 32px rgba(15, 23, 42, 0.12)',
                  border: '1px solid rgba(15, 23, 42, 0.08)',
                  overflow: 'visible',
                  '&::before': {
                    content: '""',
                    display: 'block',
                    position: 'absolute',
                    top: 0,
                    right: 14,
                    width: 10,
                    height: 10,
                    bgcolor: 'background.paper',
                    transform: 'translateY(-50%) rotate(45deg)',
                    zIndex: 0,
                    borderTop: '1px solid rgba(15, 23, 42, 0.08)',
                    borderLeft: '1px solid rgba(15, 23, 42, 0.08)',
                  },
                },
              }}
              TransitionComponent={Fade}
              transitionDuration={200}
            >
              {/* User Info Header */}
              <Box sx={{ px: 2, py: 1.5, bgcolor: 'rgba(37, 99, 235, 0.03)', borderBottom: '1px solid rgba(15, 23, 42, 0.06)' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                  {user?.fullName || user?.name || 'User'}
                </Typography>
                <Chip
                  label={roleConfig.label}
                  size="small"
                  style={{
                    backgroundColor: roleConfig.bg,
                    color: roleConfig.color,
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    height: 20,
                  }}
                />
              </Box>
              
              {/* Profile and Settings - only for commercial and admin users */}
              {hasSettingsAccess && (
                <>
                  <MenuItem 
                    onClick={() => { handleClose(); navigate('/settings/profile'); }}
                    sx={{
                      py: 1.5,
                      px: 2,
                      transition: 'all 0.15s ease',
                      '&:hover': {
                        bgcolor: 'rgba(37, 99, 235, 0.08)',
                      }
                    }}
                  >
                    <PersonIcon fontSize="small" sx={{ mr: 1.5, color: '#64748B' }} />
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#334155' }}>
                      {t("Profile")}
                    </Typography>
                  </MenuItem>
                  <MenuItem 
                    onClick={() => { handleClose(); navigate('/settings/profile'); }}
                    sx={{
                      py: 1.5,
                      px: 2,
                      transition: 'all 0.15s ease',
                      '&:hover': {
                        bgcolor: 'rgba(37, 99, 235, 0.08)',
                      }
                    }}
                  >
                    <SettingsIcon fontSize="small" sx={{ mr: 1.5, color: '#64748B' }} />
                    <Typography variant="body2" sx={{ fontWeight: 500, color: '#334155' }}>
                      {t("Settings")}
                    </Typography>
                  </MenuItem>
                </>
              )}
              
              <Divider sx={{ my: 0.5, borderColor: 'rgba(15, 23, 42, 0.08)' }} />
              
              {/* Logout with warning styling */}
              <MenuItem 
                onClick={handleLogout}
                sx={{
                  py: 1.5,
                  px: 2,
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    bgcolor: 'rgba(239, 68, 68, 0.1)',
                  }
                }}
              >
                <LogoutIcon fontSize="small" sx={{ mr: 1.5, color: '#ef4444' }} />
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#ef4444' }}>
                  {t("Logout")}
                </Typography>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBarStyled>
      <ContentContainer hideSidebar={hideSidebar} collapsed={sidebarCollapsed ? 1 : 0}>
        {children}
      </ContentContainer>
      
      {/* Logout Confirmation Dialog */}
      <Dialog
        open={logoutDialogOpen}
        onClose={handleLogoutCancel}
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxWidth: 400,
            boxShadow: '0 20px 60px rgba(15, 23, 42, 0.15)',
          }
        }}
        TransitionComponent={Fade}
        transitionDuration={300}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box 
              sx={{ 
                width: 48, 
                height: 48, 
                borderRadius: '50%', 
                bgcolor: 'rgba(239, 68, 68, 0.1)', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center' 
              }}
            >
              <WarningIcon sx={{ color: '#ef4444', fontSize: 28 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
              {t("Confirm Logout")}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 1, pb: 2 }}>
          <Typography variant="body2" sx={{ color: '#64748b', lineHeight: 1.6 }}>
            {t("Are you sure you want to log out? You will need to sign in again to access your account.")}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
          <Button 
            onClick={handleLogoutCancel}
            sx={{
              px: 3,
              py: 1,
              borderRadius: 2,
              fontWeight: 600,
              color: '#64748b',
              '&:hover': {
                bgcolor: 'rgba(100, 116, 139, 0.08)',
              }
            }}
          >
            {t("Cancel")}
          </Button>
          <Button 
            onClick={handleLogoutConfirm}
            variant="contained"
            sx={{
              px: 3,
              py: 1,
              borderRadius: 2,
              fontWeight: 600,
              bgcolor: '#ef4444',
              '&:hover': {
                bgcolor: '#dc2626',
              }
            }}
            startIcon={<LogoutIcon fontSize="small" />}
          >
            {t("Logout")}
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default MainLayout;
